import { useCallback, useEffect, useState } from 'react';

import {
  clearOtpCooldown,
  getOtpCooldownRemaining,
  setOtpCooldown,
} from '@/shared/lib/otp-cooldown';

type UseOtpCooldownReturn = {
  secondsLeft: number;
  isCoolingDown: boolean;
  startCooldown: (seconds: number, emailOverride?: string) => void;
  clear: () => void;
};

/**
 * Resend-cooldown countdown for the email-OTP flow, backed by a persisted
 * absolute expiry (see `otp-cooldown.ts`). Hydrates from storage on mount — so
 * a page reload or a back-to-email round-trip can't reset the timer — and
 * re-reads on the `storage` event so a second tab can't be used to bypass it.
 */
export const useOtpCooldown = (email: string): UseOtpCooldownReturn => {
  const [secondsLeft, setSecondsLeft] = useState(() => getOtpCooldownRemaining(email));

  // Re-hydrate from the persisted expiry whenever the email changes (+ mount).
  useEffect(() => {
    setSecondsLeft(getOtpCooldownRemaining(email));
  }, [email]);

  // Tick every second while cooling down. Recompute from the stored expiry
  // (truthful across tab sleep) rather than blindly decrementing.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => {
      setSecondsLeft(getOtpCooldownRemaining(email));
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft, email]);

  // Cross-tab: a cooldown set in another tab should lock this one too.
  useEffect(() => {
    const onStorage = () => setSecondsLeft(getOtpCooldownRemaining(email));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [email]);

  // `emailOverride` exists because callers (use-auth-flow's submit path) write
  // the cooldown before `userEmail` — and therefore this hook's `email`
  // closure — has settled. Passing the email explicitly writes + displays the
  // correct address regardless of the stale closure.
  const startCooldown = useCallback(
    (seconds: number, emailOverride?: string) => {
      const target = emailOverride ?? email;
      setOtpCooldown(target, seconds);
      setSecondsLeft(getOtpCooldownRemaining(target));
    },
    [email],
  );

  const clear = useCallback(() => {
    clearOtpCooldown(email);
    setSecondsLeft(0);
  }, [email]);

  return {
    secondsLeft,
    isCoolingDown: secondsLeft > 0,
    startCooldown,
    clear,
  };
};
